"use client";

import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

interface CurrencySelectorV2Props {
  className?: string;
}

export default function CurrencySelectorV2({ className = "" }: CurrencySelectorV2Props) {
  const [mounted, setMounted] = useState(false);
  const { code: currencyCode, changeCurrency, currencies: currencyOptions } = useCurrency();
  const searchParams = useSearchParams();
  const { fastPush } = useFastRouter();
  const hasSyncedUrlRef = useRef(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // 1. Sync URL param when specified in query string on initial load
  useEffect(() => {
    if (currencyOptions.length === 0 || hasSyncedUrlRef.current) return;

    const currencyFromUrl = searchParams.get("currency");
    if (currencyFromUrl) {
      hasSyncedUrlRef.current = true;
      const matched = currencyOptions.find(
        (c) => c.code?.toUpperCase() === currencyFromUrl.toUpperCase()
      );
      if (matched && matched.code?.toUpperCase() !== currencyCode?.toUpperCase()) {
        changeCurrency(matched);
      }
    }
  }, [currencyOptions, searchParams, changeCurrency, currencyCode]);



  // 2. Handle currency selection
  const handleCurrencyChange = (val: string) => {
    const selected = currencyOptions.find((c) => c.code === val);
    if (selected) {
      changeCurrency(selected);

      const params = new URLSearchParams(searchParams.toString());
      params.set("currency", val);
      fastPush(`?${params.toString()}`);
    }
  };

  return (
    <div className={`!hidden relative items-center cursor-pointer ${className}`}>

      {!mounted ? (
        <div className="flex items-center gap-1 text-slate-500 text-xs font-semibold h-auto">
          <span>USD</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-50" />
        </div>
      ) : (
        <Select value={currencyCode} onValueChange={handleCurrencyChange}>
          <SelectTrigger
            aria-label="Change currency"
            className="flex items-center gap-1 !border-none bg-transparent !shadow-none hover:text-[var(--brand-accent)] transition-colors duration-200 !outline-none !ring-0 focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none focus-visible:!border-none data-[state=open]:!border-none data-[state=open]:!ring-0 data-[state=open]:!outline-none p-0 cursor-pointer text-xs font-semibold h-auto"
          >
            <SelectValue placeholder={currencyCode || "USD"} />
          </SelectTrigger>

          <SelectContent className="z-[999] bg-white border border-slate-200 rounded-md shadow-md">
            {currencyOptions.map((c) => (
              <SelectItem key={c.id || c.code} value={c.code} className="text-xs font-semibold cursor-pointer">
                {c.symbol} {c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
